/**
 * /22sports — B2's 13th Birthday · Dunk Jam 3v3 Tourney RSVP.
 *
 * Public page (no auth). Guests pick Yes/No/Maybe and, if Yes, say
 * whether they're bringing anyone. Founder view at /22sports/admin.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, HelpCircle, Loader2, Trophy, Flame, Calendar, Clock, MapPin } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const FLYER = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_prsvr-enroll/artifacts/i5xtlh7b_IMG_1983.jpeg';

const TAG = 'text-[10px] font-black uppercase tracking-[0.28em]';

const AttendChip = ({ value, active, label, icon: Icon, onClick, color, testId }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={testId}
    className={`flex-1 flex flex-col items-center justify-center gap-1 py-4 border-2 transition-all
      ${active ? `${color.bg} ${color.border} ${color.text} shadow-lg` : 'bg-black/30 border-white/10 text-white/60 hover:border-white/30'}`}
  >
    <Icon className="w-6 h-6" />
    <span className="text-sm font-black uppercase tracking-widest">{label}</span>
  </button>
);

const Field = ({ label, children, hint }) => (
  <div>
    <label className={TAG + ' text-orange-300 mb-2 block'}>{label}</label>
    {children}
    {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
  </div>
);

export default function B2Rsvp() {
  const [name, setName] = useState('');
  const [attending, setAttending] = useState(''); // yes | no | maybe
  const [guests, setGuests] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Add your name');
    if (!attending) return toast.error('Pick Yes, No, or Maybe');
    setSubmitting(true);
    try {
      const { data } = await api.post('/rsvp/b2', {
        name: name.trim(),
        attending,
        guests: attending === 'yes' ? Number(guests || 0) : 0,
      });
      setDone(data.rsvp);
      toast.success("You're locked in — B2 gets pinged 🏀");
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went sideways';
      toast.error(typeof detail === 'string' ? detail : 'Please check the form');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const yes = done.attending === 'yes';
    const no = done.attending === 'no';
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center py-12"
          data-testid="rsvp-success-card"
        >
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center border-4
            ${yes ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400' :
              no ? 'bg-rose-500/10 border-rose-500 text-rose-400' :
              'bg-amber-500/10 border-amber-500 text-amber-400'}`}>
            {yes ? <Check className="w-10 h-10" /> : no ? <X className="w-10 h-10" /> : <HelpCircle className="w-10 h-10" />}
          </div>
          <div className={TAG + ' text-orange-400 mb-3'}>RSVP LOCKED IN</div>
          <h1 className="text-3xl font-black mb-3">
            {yes && <>See you October 9 🔥</>}
            {no && <>We'll miss you — thanks for letting us know.</>}
            {done.attending === 'maybe' && <>Got it — keep us posted.</>}
          </h1>
          <p className="text-white/60 mb-1">{done.name}</p>
          {yes && done.guests > 0 && (
            <p className="text-white/60 mb-1">Bringing <span className="text-white">{done.guests}</span> guest{done.guests === 1 ? '' : 's'}</p>
          )}
          <button
            onClick={() => { setDone(null); setName(''); setAttending(''); setGuests(0); }}
            className="mt-8 text-xs uppercase tracking-widest font-black text-orange-400 hover:text-orange-300"
            data-testid="rsvp-add-another"
          >
            + RSVP for someone else
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0a0a0f] to-black text-white">
      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-8 pb-6">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="absolute -inset-2 bg-gradient-to-br from-orange-500/40 via-red-500/20 to-blue-500/20 blur-2xl rounded-2xl" />
            <img
              src={FLYER}
              alt="B2's 13th Birthday · Dunk Jam 3v3 Tourney"
              className="relative w-full rounded-xl border-2 border-white/10 shadow-2xl"
              data-testid="rsvp-flyer"
            />
          </motion.div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className={TAG + ' text-orange-400'}>Handle Life · B2 turns 13</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-4">
              Dunk Jam <span className="text-orange-500">3v3</span> Tourney
            </h1>
            <p className="text-white/70 text-base mb-6 leading-relaxed">
              3-Point Contest · Skills Challenge · Dunk Exhibition · 3v3 Tournament.
              Tap below to let B2 know if you're rolling through.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3 text-white/80">
                <Calendar className="w-4 h-4 text-orange-500" />
                <span>Friday, October 9, 2026</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Clock className="w-4 h-4 text-orange-500" />
                <span>1:30 PM – 3:30 PM</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span>Handle Life Academy</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RSVP Form */}
      <form onSubmit={submit} className="max-w-2xl mx-auto px-4 pb-16">
        <div className="border-2 border-white/10 bg-black/60 backdrop-blur-sm p-6 sm:p-8 rounded-xl">
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-orange-500" />
            <h2 className="text-xl font-black uppercase tracking-widest">Are you in?</h2>
          </div>

          <div className="flex gap-2 mb-6">
            <AttendChip
              value="yes" active={attending === 'yes'} label="I'm in"
              icon={Check} onClick={setAttending} testId="rsvp-yes"
              color={{ bg: 'bg-emerald-500/10', border: 'border-emerald-500', text: 'text-emerald-400' }}
            />
            <AttendChip
              value="maybe" active={attending === 'maybe'} label="Maybe"
              icon={HelpCircle} onClick={setAttending} testId="rsvp-maybe"
              color={{ bg: 'bg-amber-500/10', border: 'border-amber-500', text: 'text-amber-400' }}
            />
            <AttendChip
              value="no" active={attending === 'no'} label="Can't make it"
              icon={X} onClick={setAttending} testId="rsvp-no"
              color={{ bg: 'bg-rose-500/10', border: 'border-rose-500', text: 'text-rose-400' }}
            />
          </div>

          <div className="space-y-5">
            <Field label="Your Name">
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="First and last"
                className="w-full bg-black/60 border border-white/15 focus:border-orange-500 outline-none px-3 py-3 text-white text-base rounded-md"
                data-testid="rsvp-name"
                required
              />
            </Field>

            {attending === 'yes' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="overflow-hidden"
              >
                <Field label="Bringing anyone?" hint="Guests joining you (0–10)">
                  <input
                    type="number" min={0} max={10}
                    value={guests} onChange={(e) => setGuests(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 focus:border-orange-500 outline-none px-3 py-3 text-white text-base rounded-md"
                    data-testid="rsvp-guests"
                  />
                </Field>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:opacity-60 text-black font-black text-base uppercase tracking-widest py-4 rounded-md flex items-center justify-center gap-2 transition-all"
              data-testid="rsvp-submit"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send RSVP →</>}
            </button>
            <p className="text-[11px] text-white/40 text-center mt-3">
              RSVP goes straight to the birthday crew. No spam — pinky promise.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
